import React from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { BaseView, BaseText } from '@/components/base'
import { RequestItem } from '@/components/RequestItem'
import { Request } from '@/domain/requests'
import { CurrencyCatalogsDTO } from '@/domain/currencies'

interface RequestsListProps {
  requests: Request[]
  isLoading?: boolean
  onDeclineRequest?: (requestId: string) => void
  onAcceptRequest?: (requestId: string) => void
  displayCurrency?: CurrencyCatalogsDTO
}

export const RequestsList: React.FC<RequestsListProps> = ({
  requests,
  isLoading = false,
  onDeclineRequest,
  onAcceptRequest,
  displayCurrency,
}) => {
  const { t } = useTranslation()
  const navigate = useNavigate()

  const maxListItems = 3
  const hasMoreThanMaxItems = requests.length > maxListItems
  const hasMoreThanOneItem = requests.length > 1

  if (!requests.length && !isLoading) return null

  const handleSeeAll = () => {
    navigate('/requests')
  }

  // Calculate card width like in RN version
  // 82% for multiple items, 92% for single item (matching React Native logic)
  const cardWidthPercentage = hasMoreThanOneItem ? 82 : 92
  const cardWidth = hasMoreThanOneItem ? `${cardWidthPercentage}vw` : '100%'

  return (
    <BaseView
      style={{
        gap: '16px',
        paddingBottom: '24px',
      }}
    >
      {/* Header */}
      <BaseView
        row
        justify="space-between"
        align="center"
        style={{
          paddingRight: '12px',
        }}
      >
        <BaseText
          fontSize={24}
          fontFamily="semibold"
          color="label.default"
          style={{
            paddingLeft: '12px',
            paddingRight: '12px',
          }}
        >
          {t('translation:requests') || 'Requests'}
        </BaseText>

        {hasMoreThanMaxItems && (
          <BaseView
            style={{
              cursor: 'pointer',
            }}
            onClick={handleSeeAll}
          >
            <BaseText fontSize={15} fontFamily="bold" color="label.primary">
              {t('translation:seeAll') || 'See All'}
            </BaseText>
          </BaseView>
        )}
      </BaseView>

      {/* Horizontal Scroll Container */}
      <BaseView
        style={{
          display: 'flex',
          flexDirection: 'row',
          // gap: '12px',
          overflowX: hasMoreThanOneItem ? 'auto' : 'visible',
          overflowY: 'hidden',
          paddingLeft: '12px',
          paddingRight: '12px',
          scrollbarWidth: 'none', // Firefox
          msOverflowStyle: 'none', // IE/Edge
          scrollSnapType: 'x mandatory',
        }}
      >
        {requests.slice(0, maxListItems).map((request) => (
          <div
            key={request.id}
            style={{
              display: 'flex',
              flexDirection: 'row',
              minWidth: cardWidth,
              flexShrink: 0,
              scrollSnapAlign: 'start',
            }}
          >
            {/* Невидимая левая область для отступа */}
            {hasMoreThanOneItem && (
              <div style={{ width: '13px', flexShrink: 0 }} />
            )}

            {/* Сама карточка */}
            <RequestItem
              request={request}
              onDecline={() => onDeclineRequest?.(request.id)}
              onAccept={() => onAcceptRequest?.(request.id)}
              width={
                hasMoreThanOneItem
                  ? `calc(${cardWidthPercentage}vw - 13px)`
                  : undefined
              }
              isHorizontalList={true}
              displayCurrency={displayCurrency}
              style={{
                flex: 1,
              }}
            />
          </div>
        ))}
      </BaseView>

      {/* Hide scrollbar for webkit browsers */}
      <style>{`
        div::-webkit-scrollbar {
          display: none;
        }
      `}</style>
    </BaseView>
  )
}
